import type { StateCreator } from 'zustand';
import type { ReadStatusSlice, WhatsAppChatStore } from '../types';
import { supabase } from '../../../supabase';
import { getWahaService } from '../../../services/wahaApiService';
import { debugLog } from '../../../../components/whatsapp2/chat/ChatDebugPanel';
import { normalizeChatId } from '../helpers';
import { reconcileUnreadWithWaha } from '../../../services/whatsapp/wahaReadState';

export const createReadStatusSlice: StateCreator<
  WhatsAppChatStore,
  [],
  [],
  ReadStatusSlice
> = (set, get) => ({
  sendReadReceipt: async (chatId: string) => {
    const { wahaConfig, chats } = get();
    if (!wahaConfig || !chatId) return;

    // 1. Locale: azzera unreadCount immediatamente
    const normalizedId = normalizeChatId(chatId);
    const updatedChats = chats.map(c =>
      normalizeChatId(c.id) === normalizedId ? { ...c, unreadCount: 0 } : c
    );
    set({ chats: updatedChats });

    // 2. DB: persisti lo stato letto (RPC) — fire-and-forget, cross-device sync
    supabase.auth.getUser().then(({ data }) => {
      const userId = data.user?.id;
      if (!userId) return;
      supabase.rpc('mark_whatsapp_chat_as_read', {
        p_user_id: userId,
        p_config_id: wahaConfig.configId,
        p_organization_id: wahaConfig.organizationId,
        p_chat_jid: chatId,
        p_message_id: null,
        p_timestamp: new Date().toISOString(),
      }).then(({ error }) => {
        if (error) console.error('[ReadStatus] DB mark_as_read error:', error);
      });
    }).catch((e) => console.error('[ReadStatus] auth getUser error:', e));

    // 3. Conferma di lettura al cliente (spunte blu).
    // Numero Ufficiale: via Meta (status read sull'ultimo messaggio del cliente).
    // MAI la sessione WAHA: se prima era aperto un numero Standard, il servizio
    // WAHA resta inizializzato su quella sessione e segnerebbe letta una chat
    // di un altro numero.
    const provider = get().provider;
    if (provider && provider.type !== 'whatsapp') {
      try {
        await provider.markAsRead(chatId);
      } catch {
        // Non bloccare - è solo una conferma di lettura
      }
      return;
    }
    try {
      const service = getWahaService();
      await service.markAsRead(chatId);
    } catch {
      // Non bloccare - è solo una conferma di lettura
    }
  },

  markChatAsUnread: async (chatId: string) => {
    const { wahaConfig, provider } = get();
    if (!wahaConfig || !chatId) return;
    // Meta non ha "segna come non letto" per i numeri Ufficiale: resta solo lo stato nel CRM.
    if (provider && provider.type !== 'whatsapp') return;

    try {
      const service = getWahaService();
      await service.markAsUnread(chatId);
    } catch {
      // Non bloccare
    }
  },

  setChatUnreadCount: (chatId: string, count: number) => {
    const updated = get().chats.map(c =>
      c.id === chatId ? { ...c, unreadCount: count } : c
    );
    set({ chats: updated });
  },

  loadUnreadCountsFromDb: async (userId: string) => {
    const { wahaConfig } = get();
    if (!wahaConfig) return;

    try {
      const { data, error } = await supabase.rpc('get_all_whatsapp_unread_counts', {
        p_user_id: userId,
        p_config_id: wahaConfig.configId,
      });

      if (error) {
        console.error('[Store] loadUnreadCountsFromDb error:', error);
        return;
      }

      if (get().wahaConfig !== wahaConfig) return;
      // Costruisci mappa DB counts
      const dbCounts = new Map<string, number>();
      if (data) {
        for (const row of data as Array<{ chat_jid: string; unread_count: number }>) {
          if (row.chat_jid && row.unread_count > 0) {
            dbCounts.set(row.chat_jid, Number(row.unread_count));
          }
        }
      }

      // Il DB è la source of truth: applica SEMPRE il valore DB a tutte le chat,
      // tranne quella attualmente aperta (che è sempre 0).
      // Le chat senza riga in dbCounts diventano 0.
      // Eccezione: se WAHA dice 0 non letti (chat letta dal telefono) la chat è letta,
      // e lo stato letto va riportato anche nel DB con la stessa RPC di sendReadReceipt.
      const currentSelected = get().selectedChatId;
      const chatLetteDalTelefono: string[] = [];
      const updated = get().chats.map(c => {
        if (c.id === currentSelected) return { ...c, unreadCount: 0 };
        const dbCount = dbCounts.get(c.id) ?? 0;
        const count = reconcileUnreadWithWaha(dbCount, c);
        if (dbCount > 0 && count === 0) chatLetteDalTelefono.push(c.id);
        return { ...c, unreadCount: count };
      });

      set({ chats: updated });
      for (const chatJid of chatLetteDalTelefono) {
        supabase.rpc('mark_whatsapp_chat_as_read', {
          p_user_id: userId,
          p_config_id: wahaConfig.configId,
          p_organization_id: wahaConfig.organizationId,
          p_chat_jid: chatJid,
          p_message_id: null,
          p_timestamp: new Date().toISOString(),
        }).then(({ error }) => {
          if (error) console.error('[ReadStatus] DB mark_as_read (letta dal telefono) error:', error);
        });
      }
      debugLog('Unread', `DB counts loaded: ${dbCounts.size} chat con non letti`);
    } catch (error) {
      console.error('[Store] loadUnreadCountsFromDb error:', error);
    }
  },
});
